import dayjs from 'dayjs';
import Inherit from '../../../core/inherit';
import ShelfController from '../../../core/shelf.controller';
import PromoterDelegate from './promoter.delegate';
import { calculateAverageAuditTime, fmtStoreCheck } from '../../../utils/timeUtils';

class PromoterController extends Inherit(ShelfController, PromoterDelegate) {
    async initialize() {
        let promoter = {};
        let storeChecks = [];
        let calendars = [];
        let averageMinutes = 0;
        let stores = [];

        if (!this.isAuthenticated) this.navigateLogin();

        const id = this.props.searchParams.get('id');

        if (!id) this.navigatePromoters();

        promoter = await this.data.fetchUserById(id);
        calendars = (await this.data.fetchCalendarByUser(id)) || [];
        if (calendars?.message) calendars = [];
        const { startDate, endDate } = this.getDateRangeForQuery();
        storeChecks = await this.data.fetchStoreChecksByUser(id, startDate, endDate);
        stores = await this.data.fetchAllStores();

        if (storeChecks.length > 0) averageMinutes = calculateAverageAuditTime(storeChecks);

        this.setState({ promoter, storeChecks, calendars, stores, averageMinutes, loading: false }, () => {
            this.loadTrendData(id);
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.state.date !== prevState.date ||
            this.state.filterMode !== prevState.filterMode ||
            this.state.dateRange !== prevState.dateRange
        ) {
            this.setState({ loading: true });
            this.fetchStoreChecks().then((data) => {
                this.setState({ storeChecks: data?.storeChecks, averageMinutes: data?.averageMinutes ?? 0, loading: false });
            });
        }

        if (this.state.trendRange !== prevState.trendRange) {
            const id = this.props.searchParams.get('id');
            if (id) this.loadTrendData(id);
        }

        if (this.state.trendWeekday !== prevState.trendWeekday) {
            this.rebuildTrendSeries();
        }
    }

    getDateRangeForQuery() {
        const { filterMode, date, dateRange } = this.state;
        if (filterMode === 'range' && dateRange?.[0] && dateRange?.[1]) {
            return {
                startDate: dayjs(dateRange[0]).startOf('day').toISOString(),
                endDate: dayjs(dateRange[1]).endOf('day').toISOString()
            };
        }

        const selectedDate = date ? dayjs(date) : dayjs();
        return {
            startDate: selectedDate.startOf('day').toISOString(),
            endDate: selectedDate.endOf('day').toISOString()
        };
    }

    async fetchStoreChecks() {
        const id = this.props.searchParams.get('id');
        const { startDate, endDate } = this.getDateRangeForQuery();
        const storeChecks = await this.data.fetchStoreChecksByUser(id, startDate, endDate);

        if (storeChecks.length > 0) {
            const averageMinutes = calculateAverageAuditTime(storeChecks);
            return { storeChecks: storeChecks || [], averageMinutes: averageMinutes || 0 };
        } else {
            return null;
        }
    }

    getTrendRangeForQuery() {
        const { trendRange } = this.state;
        if (trendRange?.[0] && trendRange?.[1]) {
            return {
                startDate: dayjs(trendRange[0]).startOf('day').toISOString(),
                endDate: dayjs(trendRange[1]).endOf('day').toISOString()
            };
        }

        return {
            startDate: dayjs().subtract(3, 'month').startOf('day').toISOString(),
            endDate: dayjs().endOf('day').toISOString()
        };
    }

    async loadTrendData(userId) {
        if (!userId) return;
        this.setState({ trendLoading: true });
        try {
            const { startDate, endDate } = this.getTrendRangeForQuery();
            const trendStoreChecks = await this.data.fetchStoreChecksByUser(userId, startDate, endDate);
            const trendSeries = this.buildTrendSeries(trendStoreChecks, this.state.calendars);
            this.setState({ trendStoreChecks, trendSeries });
        } catch (error) {
            console.error('Error loading trend data:', error);
            this.setState({ trendStoreChecks: [], trendSeries: [] });
        } finally {
            this.setState({ trendLoading: false });
        }
    }

    rebuildTrendSeries() {
        const { trendStoreChecks, calendars } = this.state;
        const trendSeries = this.buildTrendSeries(trendStoreChecks, calendars);
        this.setState({ trendSeries });
    }

    buildTrendSeries(storeChecks = [], calendars = []) {
        const { trendWeekday, trendRange } = this.state;
        if (!calendars || calendars.length === 0) return [];

        const normalizeDayToNumber = (dayValue) => {
            if (!dayValue) return null;
            const numeric = parseInt(dayValue, 10);
            if (!Number.isNaN(numeric)) return numeric;

            const normalized = String(dayValue).trim().toLowerCase();
            const dayMap = {
                lunes: 1,
                monday: 1,
                martes: 2,
                tuesday: 2,
                miercoles: 3,
                miércoles: 3,
                wednesday: 3,
                jueves: 4,
                thursday: 4,
                viernes: 5,
                friday: 5,
                sabado: 6,
                sábado: 6,
                saturday: 6,
                domingo: 7,
                sunday: 7,
            };
            return dayMap[normalized] || null;
        };

        const calendarByWeekday = new Map();
        calendars.forEach((calendar) => {
            const dayNumber = normalizeDayToNumber(calendar?.day);
            if (!dayNumber) return;
            const storeIds = (calendar?.stores || [])
                .map((store) => store?._id?.toString() || store?.toString())
                .filter(Boolean);
            if (!calendarByWeekday.has(dayNumber)) {
                calendarByWeekday.set(dayNumber, new Set());
            }
            const set = calendarByWeekday.get(dayNumber);
            storeIds.forEach((id) => set.add(id));
        });

        const checksByDate = new Map();
        storeChecks.forEach((check) => {
            const dateKey = fmtStoreCheck(check.createdAt, 'YYYY-MM-DD');
            if (!checksByDate.has(dateKey)) {
                checksByDate.set(dateKey, new Set());
            }
            const storeId = check?.storeId?._id?.toString() || check?.storeId?.toString();
            if (storeId) {
                checksByDate.get(dateKey).add(storeId);
            }
        });

        if (!trendRange?.[0] || !trendRange?.[1]) return [];
        const start = dayjs(trendRange[0]).startOf('day');
        const end = dayjs(trendRange[1]).endOf('day');
        if (!start.isValid() || !end.isValid()) return [];

        const series = [];
        let current = start.clone();
        const weekdayFilter = trendWeekday && trendWeekday !== 'all' ? parseInt(trendWeekday, 10) : null;

        while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
            const dayNumber = current.day(); // 0 (Sun) - 6 (Sat)
            const weekday = dayNumber === 0 ? 7 : dayNumber; // 1-7, Monday=1
            if (weekdayFilter && weekday !== weekdayFilter) {
                current = current.add(1, 'day');
                continue;
            }

            const expectedStores = calendarByWeekday.get(weekday);
            if (!expectedStores || expectedStores.size === 0) {
                current = current.add(1, 'day');
                continue;
            }

            const dateKey = current.format('YYYY-MM-DD');
            const completedStores = checksByDate.get(dateKey) || new Set();
            let completedCount = 0;
            expectedStores.forEach((storeId) => {
                if (completedStores.has(storeId)) completedCount += 1;
            });

            const percent = Math.min(100, Math.round((completedCount / expectedStores.size) * 100));
            series.push({ date: dateKey, value: percent, weekday });
            current = current.add(1, 'day');
        }

        return series;
    }

    async createCalendar(user, day, selectedStores) {
        try {
            this.setState({ calendarLoading: true });
            const response = await this.data.createCalendar({
                user,
                day,
                stores: selectedStores,
            });
            if (!response?.error) {
                let calendars = (await this.data.fetchCalendarByUser(user)) || [];
                if (calendars?.message) calendars = [];
                this.setState({ isOpen: false, calendars }, () => {
                    this.rebuildTrendSeries();
                });
            }
        } catch (err) {
            console.log(err);
        } finally {
            this.setState({ calendarLoading: false });
        }
    }
}

export default PromoterController;
